import { Button, Icon, Radio, Typography } from '@smartgift/frontend-toolkit';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Notification } from '@hero/atoms';
import { configs } from '@hero/configs';
import { CONTACTLESS_VERIFICATION_TYPES } from '@hero/constants';
import { ToggleMenu } from '@hero/molecules';
import {
  useGetRecognitionRecipientList,
  useRecogRecipientsBulkUpsert,
  useRecogRecipientsDelete,
  useUpdateRecognition
} from '@hero/services/order-service';
import { useNewRecognitionStore } from '@hero/store/hooks';

import { UploadedEmailModal } from '../../Contacts';
import { UploadEmailSpreadSheetModal } from '../../Contacts/UploadEmailSpreadSheet';

import { DomainManagement } from './DomainManagement';
import * as S from './style';

function WithoutContactRecipientVerification({
  recognitionCode,
  recipientVerification,
  updateRecipientVerification,
  recipientQuantityRef,
  recognitionMaxCount,
  sections = {}
}) {
  const { t } = useTranslation();

  const [activeMenu, setActiveMenu] = useState(recipientVerification?.type || '');
  const [emailDomains, setEmailDomains] = useState([]);

  const PhoneVerificationTitle = (
    <S.MenuTitleWrapper>
      <Radio
        id="phoneVerification"
        checked={activeMenu === CONTACTLESS_VERIFICATION_TYPES.PHONE}
        label={<Typography variant="body2">{t('phoneVerification')}</Typography>}
        readOnly
      />
    </S.MenuTitleWrapper>
  );

  const AuthorizeDomainTitle = (
    <S.MenuTitleWrapper>
      <Radio
        checked={activeMenu === CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN}
        label={<Typography variant="body2">{t('authorizeDomain')}</Typography>}
        readOnly
      />
    </S.MenuTitleWrapper>
  );

  const NoVerificationRequiredTitle = (
    <S.MenuTitleWrapper>
      <Radio
        checked={activeMenu === CONTACTLESS_VERIFICATION_TYPES.NONE}
        label={<Typography variant="body2">{t('noVerificationRequired')}</Typography>}
        readOnly
      />
    </S.MenuTitleWrapper>
  );

  useEffect(() => {
    setActiveMenu(recipientVerification?.type);
  }, [recipientVerification?.type]);

  useEffect(() => {
    setEmailDomains(recipientVerification?.params?.emailDomains || []);
  }, [recipientVerification?.params?.emailDomains]);

  return (
    <S.VerificationWrapper>
      <S.VerificationHead>
        <Typography variant="body1Bold">{t('recipientVerifications')}</Typography>
        <Typography variant="body2" color="neutral" emphasis="500">
          {t('recipientVerificationsDesc')}
        </Typography>
      </S.VerificationHead>

      {configs?.contactless?.requiredPhoneNumberEnabled && (
        <ToggleMenu
          isActive={activeMenu === CONTACTLESS_VERIFICATION_TYPES.PHONE}
          onClick={() => {
            setActiveMenu(CONTACTLESS_VERIFICATION_TYPES.PHONE);
            updateRecipientVerification({
              type: CONTACTLESS_VERIFICATION_TYPES.PHONE
            });
          }}
          title={PhoneVerificationTitle}
          hasAccordionIcon={false}
        >
          <Typography variant="body2SemiBold">{t('phoneVerificationDesc')}</Typography>
        </ToggleMenu>
      )}

      {sections.hasEmailList !== false && (
        <EmailListVerificationSection
          onMenuClick={() => {
            setActiveMenu(CONTACTLESS_VERIFICATION_TYPES.EMAIL_LIST);
            updateRecipientVerification({
              type: CONTACTLESS_VERIFICATION_TYPES.EMAIL_LIST
            });
          }}
          activeMenu={activeMenu}
          recognitionCode={recognitionCode}
          recipientQuantityRef={recipientQuantityRef}
          recognitionMaxCount={recognitionMaxCount}
        />
      )}

      <ToggleMenu
        isActive={activeMenu === CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN}
        onClick={() => {
          setActiveMenu(CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN);
          updateRecipientVerification({
            type: CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN
          });
        }}
        title={AuthorizeDomainTitle}
        hasAccordionIcon={false}
      >
        <Typography variant="body2SemiBold" style={{ marginBottom: 16 }}>
          {t('authorizeDomainDesc')}
        </Typography>
        {activeMenu === CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN && (
          <DomainManagement
            emailDomains={emailDomains}
            setEmailDomains={setEmailDomains}
            onSave={() => {
              updateRecipientVerification({
                type: CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN,
                params: {
                  emailDomains
                }
              });
            }}
          />
        )}
      </ToggleMenu>
      <ToggleMenu
        isActive={activeMenu === CONTACTLESS_VERIFICATION_TYPES.NONE}
        onClick={() => {
          setActiveMenu(CONTACTLESS_VERIFICATION_TYPES.NONE);
          updateRecipientVerification({
            type: CONTACTLESS_VERIFICATION_TYPES.NONE
          });
        }}
        title={NoVerificationRequiredTitle}
        hasAccordionIcon={false}
      >
        <Typography variant="body2SemiBold" style={{ marginBottom: 8 }}>
          {t('noVerificationRequiredDesc')}
        </Typography>
        <Notification
          status="error"
          message={
            <>
              <Typography variant="body2Bold">{t('warning')}:</Typography>
              <Trans i18nKey="noVerificationRequiredInfo" components={{ b: <b /> }} />
            </>
          }
        />
      </ToggleMenu>
    </S.VerificationWrapper>
  );
}

export { WithoutContactRecipientVerification };

function EmailListVerificationSection({
  onMenuClick,
  activeMenu,
  recognitionCode,
  recognitionMaxCount,
  recipientQuantityRef
}) {
  const { t } = useTranslation();

  const [emailListModal, setEmailListModal] = useState(false);
  const [uploadedEmailModal, setUploadedEmailModal] = useState(false);

  const { emails, updateContacts, updateEmails } = useNewRecognitionStore();

  const {
    mutateAsync: recipientBulkUpsert,
    isLoading: isBulkUpsertLoading,
    isSuccess: isBulkUpsertSuccess
  } = useRecogRecipientsBulkUpsert();
  const { mutateAsync: recipientDelete, isLoading: isDeleteLoading } = useRecogRecipientsDelete();
  const { mutateAsync: updateRecognition, isLoading: isUpdateRecognitionLoading } =
    useUpdateRecognition();

  const { data: recognitionRecipients } = useGetRecognitionRecipientList(
    { code: recognitionCode },
    { enabled: !!recognitionCode }
  );

  const totalRecipients = useMemo(() => {
    if (recognitionCode) return recognitionRecipients?.totalDocs;
    return emails?.length;
  }, [recognitionRecipients, emails?.length]);

  return (
    <ToggleMenu
      isActive={activeMenu === CONTACTLESS_VERIFICATION_TYPES.EMAIL_LIST}
      onClick={onMenuClick}
      title={
        <S.MenuTitleWrapper>
          <Radio
            checked={activeMenu === CONTACTLESS_VERIFICATION_TYPES.EMAIL_LIST}
            label={<Typography variant="body2">{t('requireEmailAddress')}</Typography>}
            readOnly
          />
        </S.MenuTitleWrapper>
      }
      hasAccordionIcon={false}
    >
      <Typography as="p" variant="body2SemiBold">
        {t('uploadEmailListDesc')}
      </Typography>
      {totalRecipients > 0 ? (
        <S.UploadedEmailArea>
          {(recipientQuantityRef || isBulkUpsertSuccess) && (
            <S.InfoBadge>
              <S.InfoIcon name="tooltip" color="var(--color-blue-50)" />
              <S.InfoTypography>
                {t('infoRequireEmailAddress')}{' '}
                <strong>
                  {recipientQuantityRef ? (
                    <Button
                      style={{ display: 'inline' }}
                      variant="link"
                      onClick={() => recipientQuantityRef.focus()}
                    >
                      {t('clickChangeRecipientQuantity')}
                    </Button>
                  ) : (
                    t('clickEditRecipientQuantity')
                  )}
                </strong>
              </S.InfoTypography>
            </S.InfoBadge>
          )}
          <S.UploadEmailArea
            style={{ padding: 16, flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Typography as="div" variant="body2" color="fg">
              {t('emailsUploaded')}:
              <Typography as="span" style={{ marginLeft: 6 }} variant="body2Bold">
                {totalRecipients}
              </Typography>
            </Typography>
            <Button
              onClick={() => {
                if (totalRecipients > 0 && !!recognitionCode) setUploadedEmailModal(true);
                else setEmailListModal(true);
              }}
              startIcon={<Icon name="edit" px="14px" />}
              variant="link"
            >
              {t('editEmailList')}
            </Button>
          </S.UploadEmailArea>
        </S.UploadedEmailArea>
      ) : (
        <S.UploadEmailArea>
          <Button
            variant="link"
            color="neutral"
            startIcon={<Icon name="upload" />}
            onClick={() => {
              setEmailListModal(true);
            }}
          >
            {t('uploadEmailSpreadsheet')}
          </Button>
          <Typography variant="body2" color="neutral" emphasis="500">
            ({t('uploadEmailListInfo')})
          </Typography>
        </S.UploadEmailArea>
      )}

      <UploadEmailSpreadSheetModal
        openModal={emailListModal}
        emails={emails}
        onRequestClose={setEmailListModal}
        onEmailUpdateDone={async emails => {
          if (recognitionCode) {
            await recipientDelete({
              code: recognitionCode
            });

            await recipientBulkUpsert({
              params: {
                code: recognitionCode
              },
              body: {
                recipients: emails.map(email => ({
                  email: email.email
                }))
              }
            });

            await updateRecognition({
              params: { recognitionCode },
              body: {
                contacts: { maxCount: emails.length.toString(), type: 'number', ids: [] }
              }
            });
            updateContacts({ maxCount: emails.length.toString(), type: 'number', ids: [] });
          } else {
            updateEmails(emails);
            updateContacts({ maxCount: emails.length.toString(), type: 'number', ids: [] });
          }
        }}
        isPrimaryButtonDisabled={
          isBulkUpsertLoading || isDeleteLoading || isUpdateRecognitionLoading
        }
      />

      <UploadedEmailModal
        recognitionCode={recognitionCode}
        recognitionMaxCount={recognitionMaxCount}
        openModal={uploadedEmailModal}
        onRequestClose={setUploadedEmailModal}
        onStartOver={() => {
          setUploadedEmailModal(false);
          setEmailListModal(true);
        }}
      />
    </ToggleMenu>
  );
}
